import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ICartRowDraft } from "types/cart";
import { UpsellGridItem } from "components/upsell/subcomponents/upsell-grid/UpsellGridItem";
import { useUpsellGroups } from "hooks/useUpsellGroups";
import { useMinWidthMediaQuery } from "hooks/useMediaQuery";
import { useDisclosure } from "@caspeco/casper-ui-library.components.modal";
import { useToast } from "@caspeco/casper-ui-library.components.toast";
import { AbortAction, ApiService } from "services/Api.service";
import { useCartAPI, useCartStore } from "store/cartStore";
import { useMenuStore } from "store/menuStore";
import { useArticleAPI } from "store/articleStore";
import { logError, logInfo } from "helpers/log-helper/log-helper";
import { getArticleQuantity } from "helpers/cart-helper/cart-helper";
import { BreakPoint } from "types/ui";
import { UpsellArticle } from "types/article";
import { LogPrefix } from "types/logging";
import { BSTLApiError } from "types/api-error";
import ArticleModal from "components/article-modal/ArticleModal";
import { UpsellDesktopContainer } from "./subcomponents/UpsellDesktopContainer";
import { UpsellModal } from "./subcomponents/UpsellModal";
import { UpsellGrid } from "./subcomponents/upsell-grid/UpsellGrid";

interface IUpsell {
	isOpen: boolean;
	onClose: () => void;
	onOpenCart: () => void;
}

/**
 * Container for Upsell UI, holds internal state for what upsell group to display.
 *
 * Renders upsell content in a Modal on smaller screens (see ./subcomponents/UpsellModal.tsx)
 * and a desktop version (see ./subcomponents/UpsellDesktopContainer.tsx)
 * on screens larger than the breakpoint "Medium" (48em).
 */
export function Upsell({ isOpen, onClose, onOpenCart }: IUpsell) {
	// TODO: Implement isLast and onNext when multiple groups support exists in BO, see BEST-3418.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { upsellGroup, isLast, onNext } = useUpsellGroups(isOpen);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const { t } = useTranslation();
	const { getArticle } = useArticleAPI();
	const { addCartRow } = useCartAPI();
	const { cart } = useCartStore();
	const { menu } = useMenuStore();
	const isLargerThanMediumBreakpoint = useMinWidthMediaQuery(BreakPoint.Medium);
	const { showToast } = useToast();
	const {
		onClose: articleModalOnClose,
		isOpen: articleModalIsOpen,
		onOpen: articleModalOnOpen,
	} = useDisclosure({
		onClose: () => {
			// Abort any ongoing requests for another article
			ApiService.cancelRequest(AbortAction.MENU_GET_ARTICLE);
		},
	});

	const onAddToCart = async (item: UpsellArticle) => {
		try {
			if (!cart) throw new Error("Cart not found on Upsell Add to Cart");
			setIsSubmitting(true);
			const cartRow: ICartRowDraft = {
				itemId: item.id,
				quantity: 1,
				extras: [],
				options: [],
			};
			await addCartRow(cart.cartId, cartRow);
			// Fake a small delay for submit state to always be visible
			setTimeout(() => {
				setIsSubmitting(false);
			}, 200);
			logInfo(LogPrefix.Cart, `Upsell Article added to Cart, Name: ${item.name}, Id: ${item.id}`);
		} catch (error) {
			logError(
				LogPrefix.Cart,
				error,
				`Could not add Upsell Article to Cart, Name: ${item.name}, Id: ${item.id}`,
			);
			showToast({
				type: "error",
				description: error instanceof BSTLApiError ? error.translatedMessage : t("error_add_to_cart"),
			});
			setIsSubmitting(false);
			throw error; // Throw error further down for grid item ui to respond
		}
	};

	const onClickArticle = (articleId: string, articleName: string) => {
		if (!menu) return;

		getArticle(menu.id, articleId, cart?.cartId).catch((error) => {
			logError(LogPrefix.Article, error, `Could not get Article, Name: ${articleName}, Id: ${articleId}`);
		});
		articleModalOnOpen();
	};

	if (!upsellGroup) return null;

	const grid = (
		<UpsellGrid
			items={upsellGroup?.articles ?? []}
			renderGridItem={(item: UpsellArticle) => (
				<UpsellGridItem
					item={item}
					quantity={getArticleQuantity(cart, item.id)}
					onAddToCart={onAddToCart}
					onOpenArticle={onClickArticle}
					isSubmitting={isSubmitting}
				/>
			)}
		/>
	);

	const articleModal = <ArticleModal isOpen={articleModalIsOpen} onClose={articleModalOnClose} />;

	if (isLargerThanMediumBreakpoint) {
		return (
			<>
				{articleModal}
				<UpsellDesktopContainer isOpen={isOpen} onClose={onClose}>
					{grid}
				</UpsellDesktopContainer>
			</>
		);
	}
	return (
		<>
			<UpsellModal isOpen={isOpen} onClose={onClose} onOpenCart={onOpenCart}>
				{grid}
				{/* Other modals must be nested within Upsell modal to be scrollable */}
				{articleModal}
			</UpsellModal>
		</>
	);
}
